import { DashboardCard, brl, fact, plural, strong } from '../../shared/dashboard/dashboard.model';
import { Month, shiftMonth } from '../../shared/month/month.service';
import { round2 } from '../../finances/finance-summary.service';
import { InvestmentClass, InvestmentMonthSummary, InvestmentOperation, InvestmentRecords, summarizeMonth, toDate } from './investments-summary.service';

export const HISTORY_MONTHS = 6;
// Fixed incomes maturing within this many days get a warning on the card
const MATURITY_WARNING_DAYS = 60;

export interface InvestmentsDashboard {
  summary: InvestmentMonthSummary,
  cards: DashboardCard[],
  history: { month: Month, buys: number, dividends: number }[]
}

export function lastMonths(month: Month, count = HISTORY_MONTHS): Month[] {
  return Array.from({ length: count }, (_, i) => shiftMonth(month, i - count + 1));
}

export function buildInvestmentsDashboard(records: InvestmentRecords, month: Month, balance: number | null, today = new Date()): InvestmentsDashboard {
  const months = lastMonths(month);
  const summaries = months.map(m => summarizeMonth(m, records, m == month ? balance : null));
  const summary = summaries[summaries.length - 1];
  const previous = summaries[summaries.length - 2];

  const classOf = (s: InvestmentMonthSummary, type: InvestmentClass) => s.classes.find(c => c.type == type);
  const buysSeries = (type: InvestmentClass) => summaries.map(s => classOf(s, type)?.buys ?? 0);
  const operations = (type: InvestmentClass) => classOf(summary, type)?.operations ?? [];

  return {
    summary,
    history: summaries.map((s, i) => ({ month: months[i], buys: s.totals.buys, dividends: s.totals.dividends })),
    cards: [
      stocksCard(operations('STOCK'), buysSeries('STOCK')),
      reitsCard(operations('REIT'), buysSeries('REIT')),
      cryptosCard(operations('CRYPTO'), buysSeries('CRYPTO')),
      fixedCard(operations('FIXED'), buysSeries('FIXED'), records.fixedIncomes, today),
      dividendsCard(summary, previous, summaries.map(s => s.totals.dividends))
    ]
  };
}

function tradeFigure(ops: InvestmentOperation[]): Pick<DashboardCard, 'figure' | 'figureNote'> {
  const buys = total(ops.filter(o => o.side == 'BUY'));
  const sells = total(ops.filter(o => o.side == 'SELL'));
  return { figure: brl(buys), figureNote: sells ? `− ${brl(sells)} vendidos` : 'em compras' };
}

function countChip(ops: InvestmentOperation[]): DashboardCard['chip'] {
  return ops.length ? { text: `${ops.length} no mês`, tone: 'info' } : null;
}

function stocksCard(ops: InvestmentOperation[], series: number[]): DashboardCard {
  const shares = distinct(ops.map(o => o.share));
  const notes = distinct(ops.map(o => o.note).filter((n): n is string => !!n));
  const taxes = round2(ops.reduce((sum, o) => sum + o.tax, 0));
  return {
    icon: 'candlestick_chart', title: 'Stocks', link: '/investments/stocks', linkLabel: 'Abrir Stocks',
    chip: countChip(ops), ...tradeFigure(ops), series,
    facts: ops.length ? [
      fact(strong(listOf(shares)), notes.length ? ` · nota ${notes.join(', ')}` : ''),
      fact(`${brl(taxes)} em taxas`)
    ] : [fact('Nenhuma operação neste mês')]
  };
}

function reitsCard(ops: InvestmentOperation[], series: number[]): DashboardCard {
  const bought = distinct(ops.filter(o => o.side == 'BUY').map(o => o.share));
  const sold = distinct(ops.filter(o => o.side == 'SELL').map(o => o.share));
  const line = [
    ...(bought.length ? ['Compra ', strong(listOf(bought))] : []),
    ...(bought.length && sold.length ? [' · '] : []),
    ...(sold.length ? ['Venda ', strong(listOf(sold))] : [])
  ];
  return {
    icon: 'apartment', title: 'REITs', link: '/investments/reits', linkLabel: 'Abrir REITs',
    chip: countChip(ops), ...tradeFigure(ops), series,
    facts: ops.length ? [fact(...line), fact('FIIs sem taxa, como no cadastro')] : [fact('Nenhuma operação neste mês')]
  };
}

function cryptosCard(ops: InvestmentOperation[], series: number[]): DashboardCard {
  const last = ops[ops.length - 1];
  const facts = last ? [
    fact(strong(`${last.amount.toLocaleString('pt-BR', { maximumFractionDigits: 8 })} ${last.share}`),
      last.btcUsd ? ` · BTC a US$ ${last.btcUsd.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''),
    ...(last.usdBrl ? [fact(`USD/BRL ${last.usdBrl.toLocaleString('pt-BR', { minimumFractionDigits: 4 })} (PTAX ${last.date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })})`)] : [])
  ] : [fact('Nenhuma operação neste mês')];
  return {
    icon: 'currency_bitcoin', title: 'Cryptos', link: '/investments/cryptos', linkLabel: 'Abrir Cryptos',
    chip: countChip(ops), ...tradeFigure(ops), series, facts
  };
}

function fixedCard(ops: InvestmentOperation[], series: number[], fixedIncomes: any[], today: Date): DashboardCard {
  // Calendar days: both dates at midnight, whatever the time saved with the maturity
  const midnight = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const start = midnight(today);
  const next = fixedIncomes
    .map(f => ({ share: f.share as string, maturity: toDate(f.maturity) }))
    .filter((f): f is { share: string, maturity: Date } => !!f.maturity && midnight(f.maturity) >= start)
    .sort((a, b) => a.maturity.getTime() - b.maturity.getTime())[0];
  const days = next ? Math.round((midnight(next.maturity) - start) / 86_400_000) : null;

  return {
    icon: 'savings', title: 'Fixed incomes', link: '/investments/fixed-incomes', linkLabel: 'Abrir Fixed incomes',
    chip: days != null && days <= MATURITY_WARNING_DAYS
      ? { text: days == 0 ? 'vence hoje' : `vence em ${plural(days, 'dia', 'dias')}`, tone: 'warn' }
      : countChip(ops),
    figure: brl(total(ops)), figureNote: 'aplicados', series,
    facts: [
      ops.length
        ? fact(strong(listOf(distinct(ops.map(o => o.share)))), ops.length == 1 ? ` · ${ops[0].profitability?.toLocaleString('pt-BR')}%` : '')
        : fact('Nenhuma aplicação neste mês'),
      next
        ? fact('Próximo vencimento ', strong(next.maturity.toLocaleDateString('pt-BR')), ` (${next.share})`)
        : fact('Nenhum vencimento futuro')
    ]
  };
}

function dividendsCard(summary: InvestmentMonthSummary, previous: InvestmentMonthSummary, series: number[]): DashboardCard {
  const byShare = new Map<string, number>();
  for (const d of summary.dividends) byShare.set(d.share, round2((byShare.get(d.share) ?? 0) + d.total));
  const largest = [...byShare.entries()].sort((a, b) => b[1] - a[1])[0];
  const change = previous.totals.dividends ? (summary.totals.dividends / previous.totals.dividends - 1) * 100 : null;

  return {
    icon: 'payments', title: 'Dividends', link: '/investments/dividends', linkLabel: 'Abrir Dividends',
    chip: summary.dividends.length ? { text: `${summary.dividends.length} no mês`, tone: 'info' } : null,
    figure: brl(summary.totals.dividends), figureNote: 'recebidos', series,
    facts: largest ? [
      fact('Maior: ', strong(largest[0]), ` ${brl(largest[1])}`),
      ...(change != null ? [fact(`${change >= 0 ? '+' : ''}${change.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}% sobre o mês anterior`)] : [])
    ] : [fact('Nenhum dividendo neste mês')]
  };
}

function total(ops: { total: number }[]): number {
  return round2(ops.reduce((sum, o) => sum + o.total, 0));
}

function distinct<T>(values: T[]): T[] {
  return [...new Set(values)];
}

// "EZTC3 e ISAE4", "A, B e mais 2"
function listOf(values: string[]): string {
  if (values.length <= 2) return values.join(' e ');
  return `${values.slice(0, 2).join(', ')} e mais ${values.length - 2}`;
}
